import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/Icon";
import Reveal from "../components/Reveal";
import SectionHead from "../components/SectionHead";
import { OPPORTUNITIES, WHY_FSG } from "../data/careers";
import { SITE } from "../data/site";
import { useL } from "../lib/useLang";

/**
 * Únete a FSG.
 * La captación de datos no vive en el sitio: cada CTA lleva al
 * Microsoft Form que el equipo ya mantiene, para que las solicitudes
 * caigan en el flujo interno que ya existe.
 */
export default function JoinFSG() {
  const { t } = useTranslation();
  const L = useL();
  const videoRef = useRef(null);
  const hasAutoplayed = useRef(false);
  const [needsSound, setNeedsSound] = useState(false);

  /** El primer toque del visitante ya autoriza el audio: lo activamos ahí. */
  const enableSound = useCallback(() => {
    const video = videoRef.current;

    if (!video) return;

    video.muted = false;
    video.volume = 1;
    setNeedsSound(false);
    video.play().catch(() => undefined);
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !("IntersectionObserver" in window)) return undefined;

    let observer;

    // Intentamos arrancar con sonido; si el navegador lo bloquea por no
    // haber interacción previa, seguimos en silencio y ofrecemos el botón
    // para activarlo, de modo que el video nunca se quede en negro.
    const play = () => {
      video.muted = false;
      video.volume = 1;

      video.play().catch(() => {
        video.muted = true;
        setNeedsSound(true);
        video.play().catch(() => undefined);
      });
    };

    const observeVideo = () => {
      observer = new window.IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || hasAutoplayed.current) return;

          hasAutoplayed.current = true;
          observer.disconnect();
          video.loop = false;
          play();
        },
        { threshold: 0.4 }
      );

      observer.observe(video);
    };

    if (document.readyState === "complete") observeVideo();
    else window.addEventListener("load", observeVideo, { once: true });

    return () => {
      window.removeEventListener("load", observeVideo);
      observer?.disconnect();
    };
  }, []);

  const formLink = {
    href: SITE.joinFormUrl,
    target: "_blank",
    rel: "noreferrer",
  };

  return (
    <section className="fsg-section fsg-section--ink fsg-join" id="unete">
      <div className="fsg-container">
        {/* 1. Encabezado principal */}
        <SectionHead
          eyebrow={t("join.eyebrow")}
          title={t("join.title")}
          lead={t("join.lead")}
          center
          as="h1"
        />

        <Reveal className="fsg-join-video">
          <div className="fsg-join-video__frame">
            <video
              ref={videoRef}
              controls
              muted={false}
              loop={false}
              playsInline
              preload="metadata"
              title={t("join.videoTitle")}
              onEnded={(event) => {
                event.currentTarget.loop = false;
                event.currentTarget.pause();
              }}
            >
              {/* El fragmento #t=0.1 pinta el primer cuadro y evita el marco negro. */}
              <source src="/video/video1.mp4#t=0.1" type="video/mp4" />
            </video>

            {needsSound && (
              <button
                type="button"
                className="fsg-join-video__sound"
                onClick={enableSound}
              >
                <Icon name="sound" />
                {t("join.videoSound")}
              </button>
            )}
          </div>
        </Reveal>

        {/* 2 y 3. Oportunidades por perfil */}
        <div className="fsg-opportunities">
          {OPPORTUNITIES.map((item, index) => (
            <Reveal
              as="article"
              className="fsg-opportunity"
              key={item.id}
              delay={index * 90}
            >
              <span className="fsg-opportunity__icon">
                <Icon name={item.icon} />
              </span>
              <span className="fsg-badge fsg-badge--invert">
                {L(item.eyebrow)}
              </span>
              <h3>{L(item.title)}</h3>
              <p>{L(item.lead)}</p>

              <ul className="fsg-checklist">
                {L(item.points).map((point) => (
                  <li key={point.title}>
                    <Icon name="check-circle" />
                    <span>
                      <strong>{point.title}:</strong> {point.text}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="fsg-card__foot">
                <a className="fsg-btn fsg-btn--primary" {...formLink}>
                  {L(item.cta)}
                  <Icon name="arrow" />
                  <span className="fsg-sr-only">{t("join.newWindow")}</span>
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        {/* 4. ¿Por qué FSG? */}
        <h2 className="fsg-join-subhead">{t("join.whyTitle")}</h2>
        <p className="fsg-join-sublead">{t("join.whyLead")}</p>

        <div className="fsg-join-benefits">
          {WHY_FSG.map((benefit, index) => (
            <Reveal
              as="article"
              className="fsg-benefit"
              key={benefit.icon + index}
              delay={index * 50}
            >
              <span className="fsg-benefit__icon">
                <Icon name={benefit.icon} />
              </span>
              <div>
                <h3>{L(benefit.title)}</h3>
                <p>{L(benefit.text)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="fsg-join-more">{t("join.whyMore")}</p>

        {/* CTA final hacia el formulario externo */}
        <Reveal className="fsg-join-cta">
          <h2>{t("join.ctaTitle")}</h2>
          <p>{t("join.ctaText")}</p>
          <a className="fsg-btn fsg-btn--primary fsg-btn--lg" {...formLink}>
            <Icon name="send" />
            {t("join.ctaButton")}
            <span className="fsg-sr-only">{t("join.newWindow")}</span>
          </a>
          <p className="fsg-join-cta__note">{t("join.ctaNote")}</p>
        </Reveal>
      </div>
    </section>
  );
}
